/**
 * WhatsApp Quick-Enquiry helper (Catalogue Phase 6).
 * Centralises the wa.me deep-link generation using the configurable company WhatsApp
 * number + templated messages from /api/company-settings.
 */

import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let _settingsCache = null;
let _settingsPromise = null;

const DEFAULTS = {
  whatsapp_number: "919999999999",
  whatsapp_enquiry_template: "Hi, I am interested in {product}{model}. Quantity: {quantity}. Please share best price and availability.",
  whatsapp_followup_template: "Hi {name}, following up on your enquiry for {product} (qty {quantity}). Let me know if you have any questions — happy to help!",
};

export async function getCompanySettings(force = false) {
  if (!force && _settingsCache) return _settingsCache;
  if (_settingsPromise) return _settingsPromise;
  _settingsPromise = axios
    .get(`${API}/company-settings`)
    .then(r => {
      _settingsCache = { ...DEFAULTS, ...(r.data || {}) };
      return _settingsCache;
    })
    .catch(() => {
      _settingsCache = { ...DEFAULTS };
      return _settingsCache;
    })
    .finally(() => { _settingsPromise = null; });
  return _settingsPromise;
}

export function invalidateCompanySettings() {
  _settingsCache = null;
}

function cleanPhone(raw) {
  return (raw || "").replace(/[^\d]/g, "");
}

function renderTemplate(tpl, vars) {
  let out = tpl;
  for (const [k, v] of Object.entries(vars)) {
    out = out.replaceAll(`{${k}}`, v == null ? "" : String(v));
  }
  return out;
}

/**
 * Build a wa.me deep-link for a product enquiry using the configured company number + template.
 * @param {object} product - product object with at least `name` (model_number optional)
 * @param {number} quantity
 * @param {object} settings - optional pre-fetched company settings
 * @returns {string} wa.me URL
 */
export function buildProductEnquiryWaLink(product, quantity = 1, settings = null) {
  const s = settings || _settingsCache || DEFAULTS;
  const msg = renderTemplate(s.whatsapp_enquiry_template || DEFAULTS.whatsapp_enquiry_template, {
    product: product?.name || "the product",
    model: product?.model_number ? ` (${product.model_number})` : "",
    quantity: quantity || 1,
  });
  return `https://wa.me/${cleanPhone(s.whatsapp_number || DEFAULTS.whatsapp_number)}?text=${encodeURIComponent(msg)}`;
}

/**
 * Build a wa.me link to a specific customer (lead follow-up scenario).
 */
export function buildLeadFollowUpWaLink(lead, settings = null) {
  const s = settings || _settingsCache || DEFAULTS;
  const msg = renderTemplate(s.whatsapp_followup_template || DEFAULTS.whatsapp_followup_template, {
    name: lead?.name || "there",
    product: lead?.product_name || "your product",
    quantity: lead?.quantity || 1,
  });
  const phone = cleanPhone(lead?.phone || "");
  return phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`
    : null;
}
